'use client';

/**
 * Automatic page view tracking for Next.js App Router.
 *
 * Usage: Put <PageTracker /> in your root layout, or call
 * usePageTracking() in a client component.
 */

import { useEffect, useRef } from 'react';
import { redactCapabilityPaths } from '../redaction';
import { useAnalytics } from './AnalyticsContext';

/**
 * Tracks page views on pathname changes.
 * Uses the browser's popstate + pushState to detect navigation
 * without depending on a specific router.
 *
 * Ruling R18: the name and the `url`/`referrer` properties are redacted HERE,
 * not only in the provider's `before_send`. The two application points do not
 * overlap — these are values WE compose and hand to `page()`, and the PostHog
 * provider forwards the name as `$current_url` rather than re-deriving it, so a
 * raw pathname handed in here would reach the wire raw. The de-dupe below still
 * compares the RAW path, so two different tables are still two page views.
 */
export function usePageTracking() {
    const { page } = useAnalytics();
    const lastPath = useRef<string>('');

    useEffect(() => {
        const trackPage = () => {
            const path = window.location.pathname;
            if (path !== lastPath.current) {
                lastPath.current = path;
                page(redactCapabilityPaths(path), {
                    url: redactCapabilityPaths(window.location.href),
                    referrer: redactCapabilityPaths(document.referrer),
                    title: document.title,
                });
            }
        };

        // Track initial page
        trackPage();

        // Listen for navigation
        window.addEventListener('popstate', trackPage);

        // Intercept pushState/replaceState for SPA navigation
        const originalPushState = window.history.pushState.bind(window.history);
        const originalReplaceState = window.history.replaceState.bind(window.history);

        window.history.pushState = (...args) => {
            originalPushState(...args);
            trackPage();
        };

        window.history.replaceState = (...args) => {
            originalReplaceState(...args);
            trackPage();
        };

        return () => {
            window.removeEventListener('popstate', trackPage);
            window.history.pushState = originalPushState;
            window.history.replaceState = originalReplaceState;
        };
    }, [page]);
}

/**
 * Drop-in component that activates page-view tracking and renders nothing.
 * Place it inside <AnalyticsProvider> (so it can read the analytics context):
 *
 *   <AnalyticsProvider provider={...}>
 *     <PageTracker />
 *     {children}
 *   </AnalyticsProvider>
 *
 * Works for both Next.js App Router and Vite SPA routers — it hooks
 * history.pushState/replaceState + popstate, independent of the router.
 */
export function PageTracker(): null {
    usePageTracking();
    return null;
}
